import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, CircularProgress, Button, Alert, Snackbar } from '@mui/material';
import { updateEncounter } from '../../../redux/slices/encountersSlice';
import { setCareTeam } from '../../../services/careCoordinationService';
import EncounterBaseRefactored, { syncEncounterToMedicalHistory } from '../EncounterBaseRefactored';
import WorkflowTaskList from '../WorkflowTaskList';
import { useEncounterFormState } from '../hooks/useEncounterFormState';
import VideoCallRoom from '../../telehealth/VideoCallRoom';
import TelehealthService from '../../../services/telehealthService';
import { useAuth } from '../../../context/AuthContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { Fullscreen, FullscreenExit, Close } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Fade from '@mui/material/Fade';
// Note: Data fetching is handled by EncounterDetails.js (parent component)

dayjs.extend(utc);
dayjs.extend(timezone);

const TelehealthEncounterDetails = ({ onRefreshEncounter }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [careTeamChanges, setCareTeamChanges] = useState(null);
  const [resetPendingChanges, setResetPendingChanges] = useState(() => () => {});
  
  // Video call state
  const [currentSession, setCurrentSession] = useState(null);
  const [isInVideoCall, setIsInVideoCall] = useState(false);
  const [videoCallLoading, setVideoCallLoading] = useState(false);
  const [videoCallError, setVideoCallError] = useState(null);
  const [videoCallModalOpen, setVideoCallModalOpen] = useState(false);
  const [videoCallTimeout, setVideoCallTimeout] = useState(false);
  const [videoCallTimerId, setVideoCallTimerId] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);

  // Data is fetched by parent component (EncounterDetails.js) and stored in Redux
  const encounter = useSelector(state => state.encounters.selectedEncounter);
  const loading = useSelector(state => state.encounters.loading);
  const error = useSelector(state => state.encounters.error);
  const selectedPatient = useSelector(state => state.patients.selectedPatient);
  const prescriptions = useSelector(state => state.prescriptions.prescriptions);
  const recentEncounters = useSelector(state => state.encounters.recentEncounters);

  // Use the refactored hook for form state management
  const {
    formData,
    setFormData,
    hasChanges,
    resetChanges
  } = useEncounterFormState(encounter, 'telehealth');

  const getDisplayDate = () => {
    if (!encounter?.createdAt) return 'Unknown Date';
    let date = new Date(encounter.createdAt);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const userTz = dayjs.tz.guess();
    return dayjs(date).tz(userTz).format('YYYY-MM-DD HH:mm');
  };

  const getAdmissionInfo = () => {
    const admissionDate = getDisplayDate();
    const platformInfo = encounter?.telehealthDetails?.platform ? `Platform: ${encounter.telehealthDetails.platform}` : 'No Platform Specified';
    return `Session: ${admissionDate} • ${platformInfo}`;
  };

  const handleStartVideoCall = async () => {
    console.log('handleStartVideoCall called with:', { 
      encounter: !!encounter, 
      selectedPatient: !!selectedPatient, 
      user: !!user,
      userDetails: user 
    });
    
    if (!encounter || !selectedPatient || !user) {
      console.error('Missing required data:', { 
        encounter: !!encounter, 
        selectedPatient: !!selectedPatient, 
        user: !!user 
      });
      return;
    }
    
    // Simple authentication check - just verify user exists
    if (!user || !user.uid) {
      console.error('No user data available');
      setVideoCallError('User information not available. Please refresh the page and try again.');
      return;
    }
    
    console.log('User authentication check passed:', {
      uid: user.uid,
      email: user.email
    });
    
    setVideoCallLoading(true);
    setVideoCallError(null);
    setVideoCallTimeout(false);
    setVideoCallModalOpen(true);
    try {
      // Check if there's already a session for this encounter
      let sessionId = encounter?.telehealthDetails?.sessionId;
      
      if (!sessionId) {
        // Create a new session
        const sessionData = {
          title: `Telehealth Visit - ${selectedPatient.name}`,
          description: encounter?.chiefComplaint || 'Telehealth consultation',
          encounterId: encounter.id,
          patientId: selectedPatient.id,
          providerId: user.uid,
          participants: [
            { id: user.uid, role: 'provider', name: user.displayName || user.email },
            { id: selectedPatient.id, role: 'patient', name: selectedPatient.name }
          ],
          settings: {
            videoQuality: '720p',
            audioQuality: 'high',
            recordingEnabled: false,
            chatEnabled: true,
            screenShareEnabled: true
          }
        };
        
        const session = await TelehealthService.createSession(sessionData);
        sessionId = session.id;
        
        // Update encounter with session ID
        await dispatch(updateEncounter({ 
          encounterId: encounter.id, 
          updateData: {
            ...encounter,
            telehealthDetails: {
              ...encounter.telehealthDetails,
              sessionId: sessionId,
              status: 'active'
            }
          }
        })).unwrap();
      }
      
      // Join the session (mark as present, but don't start WebRTC yet)
      console.log('About to join session with:', { sessionId, userId: user.uid, userRole: 'provider' });
      // TODO: Temporarily skip joinSession to test if this is causing the error
      // await TelehealthService.joinSession(sessionId, user.uid, 'provider');
      console.log('Skipping joinSession call for now');
      
      // Set current session and show VideoCallRoom (which will handle waiting/connection)
      setCurrentSession({ id: sessionId });
      setIsInVideoCall(true);
      
      // Start 5-minute timer
      if (videoCallTimerId) clearTimeout(videoCallTimerId);
      const timerId = setTimeout(() => {
        setVideoCallTimeout(true);
        setVideoCallModalOpen(false);
        setIsInVideoCall(false);
        setCurrentSession(null);
      }, 5 * 60 * 1000); // 5 minutes
      setVideoCallTimerId(timerId);
      
    } catch (error) {
      console.error('Error starting video call:', error);
      
      // Provide specific error messages
      let errorMessage = 'Failed to start video call. ';
      
      if (error.code === 'functions/unavailable') {
        errorMessage += 'Backend service is temporarily unavailable. Please try again in a few moments.';
      } else if (error.code === 'functions/deadline-exceeded') {
        errorMessage += 'Request timed out. Please check your internet connection and try again.';
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage += 'Authentication failed. Please refresh the page and try again.';
      } else if (error.message?.includes('User must be authenticated')) {
        errorMessage += 'Please refresh the page and try again.';
      } else if (error.message?.includes('not authorized')) {
        errorMessage += 'You are not authorized to start this video call.';
      } else if (error.message?.includes('Session not found')) {
        errorMessage += 'Session not found. Please refresh the page and try again.';
      } else if (error.message?.includes('User not authenticated')) {
        errorMessage += 'Please refresh the page and try again.';
      } else {
        errorMessage += 'Please check your camera and microphone permissions and try again.';
      }
      
      setVideoCallError(errorMessage);
      setVideoCallModalOpen(false);
    } finally {
      setVideoCallLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData) return;

    setSaving(true);
    try {
      const previousStatus = encounter?.status;
      const newStatus = formData.status;
      
      const updateData = {
        ...formData,
        createdAt: new Date().toISOString(),
        vitals: formData.vitals,
        notes: formData.notes,
        symptoms: formData.symptoms,
        actionPlan: formData.actionPlan,
        provider: formData.provider,
        department: formData.department,
        unit: formData.unit,
        chiefComplaint: formData.chiefComplaint,
        initialAssessment: formData.initialAssessment,
        telehealthDetails: formData.telehealthDetails
      };

      // Save encounter data
      await dispatch(updateEncounter({ encounterId: encounter.id, updateData })).unwrap();

      // Save care team changes if any
      if (careTeamChanges && encounter?.patientId) {
        await setCareTeam(encounter.patientId, careTeamChanges, 'system');
        setCareTeamChanges(null);
      }

      // Sync to medical history when encounter is completed/discharged
      // This will sync diagnoses to Problem List
      await syncEncounterToMedicalHistory({
        patientId: encounter?.patientId,
        facilityId: encounter?.facilityId || selectedPatient?.facilityId,
        encounterData: {
          ...updateData,
          type: 'telehealth',
          encounterType: 'telehealth'
        },
        encounterId: encounter.id,
        newStatus,
        previousStatus
      });

      // Reset pending changes state
      resetPendingChanges();
      resetChanges();
    } catch (error) {
      console.error('Error saving telehealth encounter:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/patients/${encounter?.patientId}/encounters`);
  };

  const renderTaskList = () => (
    <WorkflowTaskList encounterId={encounter?.id} />
  );

  // If in video call, show VideoCallRoom in a modal
  const renderVideoCallModal = () => (
    <Dialog 
      open={videoCallModalOpen} 
      onClose={handleCloseVideoCallModal} 
      maxWidth={false}
      fullWidth
      fullScreen={isMaximized}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          margin: isMaximized ? 0 : 2,
          width: isMaximized ? '100%' : '90vw',
          height: isMaximized ? '100%' : '90vh',
          maxWidth: isMaximized ? '100%' : '1200px',
          maxHeight: isMaximized ? '100%' : '900px',
          borderRadius: isMaximized ? 0 : '16px',
          overflow: 'hidden',
          transform: 'none !important',
          transition: 'all 0.2s ease-in-out !important'
        },
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
    >
              <Box sx={{ 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          backgroundColor: '#1a1a1a',
          borderRadius: isMaximized ? 0 : '16px',
          overflow: 'hidden',
          transform: 'none',
          backfaceVisibility: 'hidden',
          willChange: 'auto'
        }}>
        {/* Control Bar */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex',
          gap: 1,
          p: 1,
          background: 'rgba(0, 0, 0, 0.5)',
          borderRadius: '0 0 0 16px',
          backdropFilter: 'blur(10px)'
        }}>
          <IconButton
            onClick={() => setIsMaximized(!isMaximized)}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
            size="small"
          >
            {isMaximized ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
          <IconButton
            onClick={handleCloseVideoCallModal}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.4)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>

        {/* Video Call Content */}
        <Box sx={{ width: '100%', height: '100%' }}>
          {isInVideoCall && currentSession ? (
            <VideoCallRoom
              sessionId={currentSession.id}
              encounterId={encounter?.id}
              patientId={selectedPatient?.id}
              providerId={user?.uid}
              onCallEnd={() => {
                handleCloseVideoCallModal();
              }}
              onQualityReport={(metrics) => {
                TelehealthService.updateQualityMetrics(currentSession.id, user?.uid, metrics);
              }}
            />
          ) : (
            <Box sx={{ 
              p: 3, 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: '#1a1a1a',
              color: 'white'
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                Waiting for the other participant to join…
              </Typography>
              <CircularProgress sx={{ color: '#6b7eef' }} />
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );

  // Clean up timer on modal close or call end
  const handleCloseVideoCallModal = () => {
    setVideoCallModalOpen(false);
    setIsInVideoCall(false);
    setCurrentSession(null);
    setIsMaximized(false);
    if (videoCallTimerId) clearTimeout(videoCallTimerId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading telehealth encounter details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error loading telehealth encounter: {error}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back to Encounters
        </Button>
      </Box>
    );
  }

  if (!encounter) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Telehealth encounter not found
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back to Encounters
        </Button>
      </Box>
    );
  }

  return (
    <>
      <EncounterBaseRefactored
        mode="edit"
        encounter={encounter}
        patient={selectedPatient}
        prescriptions={prescriptions}
        recentEncounters={recentEncounters}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        onBack={handleBack}
        loading={loading}
        saving={saving}
        title="Telehealth Visit"
        subtitle={getAdmissionInfo()}
        renderTaskList={renderTaskList}
        showExtraTabs={true}
        facilityId={encounter?.facilityId || selectedPatient?.facilityId || ''}
        encounterType="telehealth"
        onCareTeamChange={setCareTeamChanges}
        onSaveSuccess={setResetPendingChanges}
        customHeaderIcon={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideoCallIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Button
              variant="contained"
              startIcon={<VideoCallIcon />}
              onClick={handleStartVideoCall}
              disabled={videoCallLoading}
              sx={{ 
                ml: 2, 
                backgroundColor: 'success.main',
                '&:hover': { backgroundColor: 'success.dark' }
              }}
            >
              {videoCallLoading ? 'Starting...' : 'Start Video Call'}
            </Button>
            <Button
              variant="outlined"
              onClick={async () => {
                try {
                  setVideoCallLoading(true);
                  // Always create a new session
                  const sessionData = {
                    title: `Telehealth Visit - ${selectedPatient.name}`,
                    description: encounter?.chiefComplaint || 'Telehealth consultation',
                    encounterId: encounter.id,
                    patientId: selectedPatient.id,
                    providerId: user.uid,
                    participants: [
                      { id: user.uid, role: 'provider', name: user.displayName || user.email },
                      { id: selectedPatient.id, role: 'patient', name: selectedPatient.name }
                    ],
                    settings: {
                      videoQuality: '720p',
                      audioQuality: 'high',
                      recordingEnabled: false,
                      chatEnabled: true,
                      screenShareEnabled: true
                    }
                  };
                  const session = await TelehealthService.createSession(sessionData);
                  // Update encounter with new session ID
                  await dispatch(updateEncounter({ 
                    encounterId: encounter.id, 
                    updateData: {
                      ...encounter,
                      telehealthDetails: {
                        ...encounter.telehealthDetails,
                        sessionId: session.id,
                        status: 'active'
                      }
                    }
                  })).unwrap();
                  setCurrentSession({ id: session.id });
                  setIsInVideoCall(false);
                  setVideoCallModalOpen(false);
                  setVideoCallLoading(false);
                  setVideoCallError(null);
                  alert('A new video link has been generated!');
                } catch (error) {
                  setVideoCallLoading(false);
                  setVideoCallError('Failed to regenerate video link: ' + (error.message || error));
                }
              }}
              sx={{ ml: 1 }}
            >
              Regenerate Video Link
            </Button>
          </Box>
        }
        onRefreshEncounter={onRefreshEncounter}
        hasChanges={hasChanges}
        showPatientInfoInHeader={false}
      />
      {renderVideoCallModal()}
      {/* Error Snackbar */}
      <Snackbar
        open={!!videoCallError}
        autoHideDuration={6000}
        onClose={() => setVideoCallError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setVideoCallError(null)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {videoCallError}
        </Alert>
      </Snackbar>
      {/* Show timeout message if needed */}
      {videoCallTimeout && (
        <Snackbar
          open={videoCallTimeout}
          autoHideDuration={6000}
          onClose={() => setVideoCallTimeout(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setVideoCallTimeout(false)} 
            severity="error" 
            sx={{ width: '100%' }}
          >
            The other participant did not join within 5 minutes. Please try again later.
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default TelehealthEncounterDetails; 